import Drawer from '@material-ui/core/Drawer';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {RunState} from '@wandb/weave/components/RunState';
import {capitalizeFirst} from '@wandb/weave/core/util/string';
import React, {useEffect, useMemo, useState} from 'react';

import {
  Maybe,
  RunQueueItemInfoDetails as RunQueueItemIssueDetails,
} from '../../../generated/graphql';
import {agent as agentUrl, runLogs as runLogsUrl} from '../../../util/urls';
import {ResizableDrawer} from '../../common/ResizableDrawer/ResizableDrawer';
import * as S from './RunQueueItemIssueDrawer.styles';
import {RunQueueItemModalLogViewer} from './RunQueueItemModalLogViewer';
import {InfoOption, SelectRunQueueItemInfo} from './SelectRunQueueItemInfo';

export type RunQueueItemIssueDrawerProps = {
  open: boolean;
  entityName: string;
  projectName: string;
  associatedRunId: string;
  agentRun: string;
  agentId: string;
  runName?: string;
  displayState: string;
  errorInfo: Maybe<RunQueueItemIssueDetails>;
  warningInfos: Array<Maybe<RunQueueItemIssueDetails>>;
  onClose: () => void;
};

export const RunQueueItemIssueDrawer = ({
  open,
  entityName,
  projectName,
  associatedRunId,
  agentRun,
  agentId,
  runName,
  displayState,
  errorInfo,
  warningInfos,
  onClose,
}: RunQueueItemIssueDrawerProps) => {
  const [width, setWidth] = useState(800); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const selectOptions = useMemo(() => {
    const ret: InfoOption[] = [];
    if (errorInfo != null) {
      ret.push({
        ...errorInfo,
        type: 'Error',
        value: errorInfo.filePaths[0],
      });
    }
    warningInfos.forEach(warning => {
      if (warning != null) {
        ret.push({
          ...warning,
          type: 'Warning',
          value: warning.filePaths[0],
        });
      }
    });
    return ret;
  }, [errorInfo, warningInfos]);

  const [viewedErrorOrWarning, setViewedErrorOrWarning] =
    useState<InfoOption | null>(
      selectOptions.length > 0 ? selectOptions[0] : null
    );

  useEffect(() => {
    setViewedErrorOrWarning(selectOptions.length > 0 ? selectOptions[0] : null);
  }, [errorInfo, warningInfos, selectOptions]);

  if (selectOptions.length === 0 || viewedErrorOrWarning == null) {
    return null;
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} variant="persistent">
      <ResizableDrawer
        startWidth={width}
        minWidth={400}
        onSetWidth={onSetWidth}>
        <S.DrawerContents>
          <S.DrawerMain>
            <S.HeaderRow>
              <S.Header>Issue details</S.Header>
              <S.RightButton>
                <Button
                  data-test="rqi-info-drawer-close"
                  onClick={onClose}
                  icon="close"
                  size="large"
                  variant="ghost"
                />
              </S.RightButton>
            </S.HeaderRow>
            <S.UnderLine />
            <div style={{display: 'flex', alignItems: 'center'}}>
              <S.RunName>{'Run ' + (runName ?? '')}</S.RunName>
              <RunState value={displayState} />
            </div>
            <SelectRunQueueItemInfo
              onSelectInfo={setViewedErrorOrWarning}
              options={selectOptions}
              defaultInfoOption={selectOptions[0]}
              selected={viewedErrorOrWarning}
            />
            <S.SectionTitle>Full error</S.SectionTitle>
            <S.SectionText>{viewedErrorOrWarning.message}</S.SectionText>
            <S.SectionTitle>Location</S.SectionTitle>
            <S.SectionText>
              {capitalizeFirst(viewedErrorOrWarning.stage)}
            </S.SectionText>
            {viewedErrorOrWarning.stage === 'agent' && (
              <TargetBlank
                href={agentUrl(entityName, agentId, 'logs')}
                style={{display: 'inline-block', marginLeft: '8px'}}>
                <div>
                  <Button
                    variant="secondary"
                    size="small"
                    icon="job-program-code">
                    View agent logs
                  </Button>
                </div>
              </TargetBlank>
            )}

            {viewedErrorOrWarning.stage === 'run' && runName != null && (
              <TargetBlank
                href={runLogsUrl({
                  entityName,
                  projectName,
                  name: associatedRunId,
                })}
                style={{display: 'inline-block', marginLeft: '8px'}}>
                <div>
                  <Button
                    variant="secondary"
                    size="small"
                    icon="job-program-code">
                    View run logs
                  </Button>
                </div>
              </TargetBlank>
            )}

            {viewedErrorOrWarning.filePaths.length > 0 && (
              <>
                {viewedErrorOrWarning.type === 'Error' ? (
                  <S.SectionTitle>Stack Trace</S.SectionTitle>
                ) : (
                  <S.SectionTitle>Warning Message</S.SectionTitle>
                )}
                <RunQueueItemModalLogViewer
                  entityName={entityName}
                  runName={agentRun}
                  filePath={viewedErrorOrWarning.filePaths[0]}
                />
              </>
            )}
          </S.DrawerMain>
          <div>
            <S.UnderLine />
            <Button
              className="w-full"
              variant="primary"
              size="large"
              onClick={onClose}
              style={{marginTop: '32px', marginBottom: '16px'}}>
              Done
            </Button>
          </div>
        </S.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};
